import api from '@/services/utils/apiInstance.ts';
import { displayNonNotFoundError } from '@/utils/apiErrorUtils.ts';

import type { CustomAxiosRequestConfig } from '@/types/services/utils/axios.type.ts';
import type { ClientDeclaration, ClientDeclarationResponseApi } from '@/types/services/user/clientDeclaration.type.ts';

class ClientDeclarationService {
  // Returns `null` if the client hasn't filled in the questionnaire yet (404, format B —
  // `{ detail: "..." }`, no `key`). Treated as an empty state, not an error.
  async getClientDeclaration(userId: number): Promise<ClientDeclaration | null> {
    try {
      const response = await api.get(
        `/client-declaration/bo/${userId}`,
        { disableGlobalError: true } as CustomAxiosRequestConfig
      )
      const d: ClientDeclarationResponseApi = response.data

      return {
        id: d.id,
        clientType: d.client_type,
        legalEntityType: d.legal_entity_type,
        hasClearBeneficialOwner: d.has_clear_beneficial_owner,
        legalEntityName: d.legal_entity_name,
        legalEntityAddress: d.legal_entity_address,
        legalEntityRcsNumber: d.legal_entity_rcs_number,
        legalRepresentativeName: d.legal_representative_name,
        countryOfResidence: d.country_of_residence,
        job: d.job,
        profession: d.profession,
        professionalSector: d.professional_sector,
        fundsOrigin: d.funds_origin,
        fundsOriginOtherDetail: d.funds_origin_other_detail,
        wealthOrigin: d.wealth_origin,
        maritalStatus: d.marital_status,
        matrimonialRegime: d.matrimonial_regime,
        partnerLastName: d.partner_last_name,
        partnerFirstName: d.partner_first_name,
        partnerBirthDate: d.partner_birth_date,
        partnerProfession: d.partner_profession,
        children: (d.children ?? []).map(c => ({
          lastName: c.last_name,
          firstName: c.first_name,
          birthDate: c.birth_date
        })),
        annualIncomeAmount: d.annual_income_amount,
        householdIncomeAmount: d.household_income_amount,
        incomeSources: (d.income_sources ?? []).map(s => ({ incomeSource: s.income_source })),
        annualChargesAmount: d.annual_charges_amount,
        credits: (d.credits ?? []).map(c => ({
          monthlyPayment: c.monthly_payment,
          outstandingCapital: c.outstanding_capital
        })),
        monthlySavingsAmount: d.monthly_savings_amount,
        netWorthAmount: d.net_worth_amount,
        splitNonFinancialPct: d.split_non_financial_pct,
        splitUsageGoodsPct: d.split_usage_goods_pct,
        splitPlacementSavingsPct: d.split_placement_savings_pct,
        splitTraditionalPct: d.split_traditional_pct,
        investedAmount: d.invested_amount,
        paymentMethod: d.payment_method,
        hasStockMarketExperience: d.has_stock_market_experience,
        hasFinancialSectorExperience: d.has_financial_sector_experience,
        financialSectorExperienceRole: d.financial_sector_experience_role,
        financialInstrumentsKnowledge: d.financial_instruments_knowledge,
        priorServices: d.prior_services ?? [],
        priorServicesOtherDetail: d.prior_services_other_detail,
        managedAssetCategory: d.managed_asset_category,
        cryptoAdviceOutcome: d.crypto_advice_outcome,
        cryptoAllocationRatio: d.crypto_allocation_ratio,
        wealthInvestmentRatio: d.wealth_investment_ratio,
        financialTransactionsDetail: d.financial_transactions_detail,
        incomeCoversCharges: d.income_covers_charges,
        guaranteeDisclaimerAcknowledged: d.guarantee_disclaimer_acknowledged,
        distributionChannel: d.distribution_channel
      }
    } catch (error) {
      displayNonNotFoundError(error)
      return null
    }
  }
}

export default new ClientDeclarationService();
